import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useQzTray } from '../context/QzTrayContext';
import MarkUsedModal from '../components/MarkUsedModal';
import RestockPickerModal from '../components/RestockPickerModal';
import './OrdersNewPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

async function parseJsonSafe(res) {
  try { return await res.json(); } catch (_) { return null; }
}

function formatQty(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0';
  if (Math.abs(num % 1) < 1e-9) return String(Math.trunc(num));
  return num.toFixed(2).replace(/\.?0+$/, '');
}

function formatCreatedAt(msOrIso) {
  const ms = typeof msOrIso === 'number' ? msOrIso : Date.parse(msOrIso || '');
  if (!Number.isFinite(ms) || ms <= 0) return '-';
  return new Date(ms).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Calcutta',
  });
}

function getOrderNumber(row) {
  return String(
    row?.sourceLinks?.shopify?.orderName ||
    row?.sourceLinks?.zoho?.referenceNumber ||
    row?.externalOrderId ||
    row?.id || '-'
  ).trim() || '-';
}

function getSourceCreatedAtMs(row) {
  const v = row?.sourceCreatedAtMs ?? row?.sourceCreatedAt;
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string') { const ms = Date.parse(v); if (Number.isFinite(ms)) return ms; }
  return 0;
}

function statusTone(status) {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'DELIVERED' || s === 'CLOSED') return 'green';
  if (['IN_TRANSIT', 'PACKED', 'READY_TO_PACK', 'AWAITING_SHIPMENT_WEBHOOK'].includes(s)) return 'blue';
  if (['PRODUCTION', 'ALLOCATING'].includes(s)) return 'amber';
  if (s === 'ON_HOLD') return 'red';
  if (s === 'IMPORTED') return 'slate';
  if (s.includes('RTO') || s.includes('RETURN') || s === 'CANCELLED') return 'red';
  return 'slate';
}

function formatStatus(status) {
  return String(status || '-').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatWarningAge(hours) {
  if (hours < 24) {
    return `${formatQty(hours)}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);
  return `${days}d ${remainingHours}h`;
}

function isPreShip(status) {
  return ['IMPORTED', 'ALLOCATING', 'PRODUCTION', 'READY_TO_PACK', 'PACKED', 'AWAITING_SHIPMENT_WEBHOOK'].includes(
    String(status || '').trim().toUpperCase()
  );
}

function getExcessTargetQty(line = {}) {
  const shipped = Math.max(0, Number(line?.shippedQty || 0));
  return shipped > 1e-6 ? shipped : Math.max(0, Number(line?.netRequiredQty || 0));
}

function compactIdList(ids = [], limit = 2) {
  const list = Array.isArray(ids) ? ids.map((id) => String(id || '').trim()).filter(Boolean) : [];
  if (list.length <= limit) return list.join(', ');
  return `${list.slice(0, limit).join(', ')} +${list.length - limit} more`;
}

function shouldCheckStockEligibility(order = {}, line = {}) {
  const orderStatus = String(order?.orderStatus || '').trim().toUpperCase();
  if (!['IMPORTED', 'ALLOCATING', 'PRODUCTION'].includes(orderStatus)) return false;
  const netReq = Math.max(0, Number(line?.netRequiredQty || 0));
  const received = Math.max(0, Number(line?.receivedQty || 0));
  const inBatch = Math.max(0, Number(line?.inBatchQty || 0));
  const packed = Math.max(0, Number(line?.packedQty || 0));
  const shipped = Math.max(0, Number(line?.shippedQty || 0));
  const cancelled = Math.max(0, Number(line?.cancelledQty || 0));
  const lineStatus = String(line?.lineStatus || '').trim().toUpperCase();
  const pending = Math.max(0, netReq - received - inBatch);
  if (netReq <= 1e-6) return false;
  if (pending <= 1e-6) return false;
  if (inBatch > 1e-6) return false;
  if (packed > 1e-6 || shipped > 1e-6) return false;
  if (cancelled >= netReq - 1e-6) return false;
  if (['IN_BATCH', 'PACKED', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'RTO_RETURNED', 'CUSTOMER_RETURNED'].includes(lineStatus)) return false;
  return true;
}

const STATUS_PILLS = [
  { value: 'ALL', label: 'All' },
  { value: 'IMPORTED', label: 'Imported' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'SALES_ORDER_PENDING', label: 'SO Pending' },
  { value: 'INVOICE_PENDING', label: 'Invoice Pending' },
  { value: 'INVOICE_REVIEW', label: 'Invoice Review' },
  { value: 'WARNINGS', label: 'Warnings' },
  { value: 'ALLOCATING', label: 'Allocating' },
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'READY_TO_PACK', label: 'Ready To Pack' },
  { value: 'PACKED', label: 'Packed' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'RTO_IN_PROGRESS', label: 'RTO In Progress' },
  { value: 'RTO_RECEIVED', label: 'RTO Received' },
  { value: 'CUSTOMER_RETURN_IN_PROGRESS', label: 'Customer Return' },
  { value: 'RETURN_RECEIVED', label: 'Return Received' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'CLOSED', label: 'Closed' },
];

const WARNING_SUB_PILLS = [
  { value: 'ALL', label: 'All Warnings' },
  { value: 'ALLOCATING', label: 'Allocating' },
  { value: 'PRODUCTION', label: 'Production' },
  { value: 'READY_TO_PACK', label: 'Ready to Pack' },
  { value: 'PACKED', label: 'Packed' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'RTO_IN_PROGRESS', label: 'RTO In Progress' },
];

const PAGE_SIZE = 25;

const NEW_ORDERS_LIST_CACHE_PREFIX = 'new_orders_list_cache:';
const NEW_ORDERS_DETAIL_CACHE_PREFIX = 'new_orders_detail_cache:';

function readJsonCache(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) { return null; }
}

function writeJsonCache(key, value) {
  try { window.sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* ignore */ }
}

// ── Main Component ────────────────────────────────────────────────────────────


export default function OrdersNewPage() {
  const { currentUser, canPerformAction } = useAuth();
  const { printSticker } = useQzTray() || {};

  // ── List state ──
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const [listMsg, setListMsg] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // ── Filters ──
  const [draftSearch, setDraftSearch] = useState('');
  const [draftSearchField, setDraftSearchField] = useState('orderNumber');
  const [draftStatus, setDraftStatus] = useState('ALL');
  const [draftWarningType, setDraftWarningType] = useState('ALL');
  const [applied, setApplied] = useState({ search: '', searchField: 'orderNumber', status: 'ALL', warningType: 'ALL' });

  const listCacheKey = useMemo(
    () => `${NEW_ORDERS_LIST_CACHE_PREFIX}${encodeURIComponent(JSON.stringify(applied || {}))}`,
    [applied]
  );

  // ── Expanded card ──
  const [expandedOrderId, setExpandedOrderId] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMsg, setDetailMsg] = useState('');
  const [lineEligibility, setLineEligibility] = useState({});
  const [copiedOrderId, setCopiedOrderId] = useState('');

  // ── Mark Used modal ──
  const [markUsedOpen, setMarkUsedOpen] = useState(false);
  const [markUsedCtx, setMarkUsedCtx] = useState(null);
  const [markUsedPieces, setMarkUsedPieces] = useState([]);
  const [markUsedLoading, setMarkUsedLoading] = useState(false);
  const [markUsedSubmitting, setMarkUsedSubmitting] = useState(false);

  // ── Restock picker (B3 unified piece-level) ──
  const [restockPickerOpen, setRestockPickerOpen] = useState(false);
  const [restockPickerCtx, setRestockPickerCtx] = useState({ sku: '', orderId: '', orderNumber: '', productImage: '', productName: '' });

  // ── Process PO modal ──
  const [processPoOpen, setProcessPoOpen] = useState(false);
  const [processPoLines, setProcessPoLines] = useState([]);
  const [processPoSelected, setProcessPoSelected] = useState([]);
  const [processPoSubmitting, setProcessPoSubmitting] = useState(false);

  // ── Pack modal ──
  const [packOpen, setPackOpen] = useState(false);
  const [packAwb, setPackAwb] = useState('');
  const [packPartner, setPackPartner] = useState('delhivery');
  const [packSubmitting, setPackSubmitting] = useState(false);

  // ── Cancel confirm ──
  const [cancelConfirmOrderId, setCancelConfirmOrderId] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // ── Status counts ──
  const [statusCounts, setStatusCounts] = useState({});
  const [statusTotalCount, setStatusTotalCount] = useState(0);

  // ── Warnings state ──
  const [detailWarnings, setDetailWarnings] = useState([]);
  const [detailWarningsLoading, setDetailWarningsLoading] = useState(false);

  // ── Auto-refresh ──
  const autoRefreshRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // ── Permissions ──
  const canMarkUsed = canPerformAction('orders.markUsed') || canPerformAction('ordersNew.markUsed');
  const canUndoUsed = canPerformAction('orders.undoUsed') || canPerformAction('ordersNew.undoUsed');
  // canShip gates the pack modal — uses markPacked permission on this page
  const canMarkPacked = (canPerformAction('orders.markPacked') || canPerformAction('ordersNew.markPacked'))
    && (canPerformAction('orders.ship') || canPerformAction('ordersNew.ship'));
  const canCancelShipment = canPerformAction('orders.cancelShipment') || canPerformAction('ordersNew.cancelShipment');
  const canRestock = canPerformAction('orders.restock') || canPerformAction('ordersNew.restock');
  const canUndoRestock = canPerformAction('orders.undoRestock') || canPerformAction('ordersNew.undoRestock');
  const canProcessPO = canPerformAction('orders.processPO') || canPerformAction('ordersNew.processPO') || canPerformAction('v9Orders.processPO');
  const canUndoProcessPO = canPerformAction('orders.undoProcessPO') || canPerformAction('ordersNew.undoProcessPO');
  const canManualStatus = canPerformAction('ordersNew.manualStatus');
  const canSnoozeWarning = canPerformAction('ordersNew.snoozeWarning');

  // ── Auth fetch ──
  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const hasBody = Object.prototype.hasOwnProperty.call(options, 'body');
    return fetch(url, {
      ...options,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  // ── Build URL ──
  const buildUrl = useCallback((cursor = '') => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    if (applied.status !== 'ALL') params.set('status', applied.status);
    if (applied.status === 'WARNINGS' && applied.warningType !== 'ALL') {
      params.set('warningType', applied.warningType);
    }
    if (applied.search) {
      params.set('search', applied.search);
      params.set('searchField', applied.searchField || 'orderNumber');
    }
    if (cursor) params.set('cursor', cursor);
    return `/api/v9/orders?${params.toString()}`;
  }, [applied]);

  // ── Load list ──
  const loadFirstPage = useCallback(async ({ preferCache = false, silent = false } = {}) => {
    let hasCached = false;
    if (preferCache) {
      const cached = readJsonCache(listCacheKey);
      const cachedItems = Array.isArray(cached?.items) ? cached.items : [];
      if (cachedItems.length > 0 || cached?.hasMore === true) {
        hasCached = true;
        setRows(cachedItems);
        setHasMore(cached?.hasMore === true);
        setNextCursor(String(cached?.nextCursor || ''));
        if (!silent) setListMsg('Showing cached orders. Updating...');
      }
    }
    if (!silent) {
      setLoading(!hasCached);
      if (!hasCached) setListMsg('Loading orders...');
    }
    try {
      const res = await authedFetch(buildUrl());
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        if (!silent) setListMsg(data?.error?.message || 'Failed to load orders.');
        if (!hasCached) {
          setRows([]);
          setHasMore(false);
          setNextCursor('');
        }
        return;
      }
      const items = Array.isArray(data.result?.items) ? data.result.items : [];
      // Re-sort by sourceCreatedAtMs desc (created time)
      items.sort((a, b) => getSourceCreatedAtMs(b) - getSourceCreatedAtMs(a));
      setRows(items);
      setHasMore(data.result?.hasMore === true);
      const freshNextCursor = String(data.result?.nextCursor || '');
      setNextCursor(freshNextCursor);
      setLastRefreshedAt(Date.now());
      if (!silent) setListMsg('');

      writeJsonCache(listCacheKey, {
        items,
        hasMore: data.result?.hasMore === true,
        nextCursor: freshNextCursor,
        updatedAt: Date.now(),
      });
    } catch (err) {
      if (!silent) setListMsg(err?.message || 'Failed to load orders.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authedFetch, buildUrl, listCacheKey]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await authedFetch(buildUrl(nextCursor));
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) return;
      const items = Array.isArray(data.result?.items) ? data.result.items : [];
      items.sort((a, b) => getSourceCreatedAtMs(b) - getSourceCreatedAtMs(a));
      setRows((prev) => {
        const merged = [...prev, ...items];
        writeJsonCache(listCacheKey, {
          items: merged,
          hasMore: data.result?.hasMore === true,
          nextCursor: String(data.result?.nextCursor || ''),
          updatedAt: Date.now(),
        });
        return merged;
      });
      setHasMore(data.result?.hasMore === true);
      setNextCursor(String(data.result?.nextCursor || ''));
    } finally {
      setLoadingMore(false);
    }
  }, [authedFetch, buildUrl, hasMore, listCacheKey, loadingMore, nextCursor]);

  // ── Load order warnings ──
  const loadOrderWarnings = useCallback(async (orderId) => {
    const normalizedOrderId = String(orderId || '').trim();
    if (!normalizedOrderId) return;
    setDetailWarningsLoading(true);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(normalizedOrderId)}/stale-warnings`);
      const data = await parseJsonSafe(res);
      if (res.ok && data?.ok) {
        setDetailWarnings(Array.isArray(data?.result?.rows) ? data.result.rows : []);
      } else {
        setDetailWarnings([]);
      }
    } catch (_) {
      setDetailWarnings([]);
    } finally {
      setDetailWarningsLoading(false);
    }
  }, [authedFetch]);

  // ── Load order detail ──
  const loadDetail = useCallback(async (orderId, { silent = false } = {}) => {
    if (!silent) {
      setDetailLoading(true);
      setDetailMsg('');
      setDetailData(null);
      setLineEligibility({});
      loadOrderWarnings(orderId);
    }
    const detailCacheKey = `${NEW_ORDERS_DETAIL_CACHE_PREFIX}${orderId}`;
    const cached = readJsonCache(detailCacheKey);
    let hasCached = false;
    if (cached?.detailData?.order) {
      hasCached = true;
      setDetailData(cached.detailData);
      setLineEligibility(cached.lineEligibility || {});
      if (!silent) setDetailMsg('');
    }
    if (!silent) setDetailLoading(!hasCached);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}`);
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        if (!silent) setDetailMsg(data?.error?.message || 'Failed to load order.');
        if (!hasCached) setDetailData(null);
        return;
      }
      const result = data.result || null;
      setDetailData(result);
      
      // Fetch line eligibility (stock-candidates) for eligible lines
      const order = result?.order || result || {};
      const lines = Array.isArray(result?.lines) ? result.lines : [];
      const checksNeeded = lines.filter((line) => shouldCheckStockEligibility(order, line));
      let eligMap = {};
      if (checksNeeded.length > 0) {
        const checks = await Promise.all(checksNeeded.map(async (line) => {
          try {
            const r = await authedFetch(`/api/v9/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(line.sku)}/stock-candidates`);
            const d = await parseJsonSafe(r);
            return { sku: line.sku, result: (r.ok && d?.ok) ? (d.result || null) : null };
          } catch (_) { return { sku: line.sku, result: null }; }
        }));
        lines.forEach((line) => { eligMap[line.sku] = { canMarkUsed: false, totalAvailable: 0, pieces: [] }; });
        checks.forEach(({ sku, result: r }) => { if (r) eligMap[sku] = r; });
        setLineEligibility(eligMap);
      }
      
      writeJsonCache(detailCacheKey, {
        detailData: result,
        lineEligibility: eligMap,
        updatedAt: Date.now(),
      });
    } catch (err) {
      if (!silent) setDetailMsg(err?.message || 'Failed to load order.');
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, [authedFetch, loadOrderWarnings]);

  // ── Toggle expand ──
  const toggleExpand = useCallback((orderId) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId('');
      setDetailData(null);
      setDetailMsg('');
      setLineEligibility({});
      setDetailWarnings([]);
    } else {
      setExpandedOrderId(orderId);
      setDetailWarnings([]);
      loadDetail(orderId);
    }
  }, [expandedOrderId, loadDetail]);

  // ── callAction ──
  const callAction = useCallback(async (path, body = null, successMsg = 'Done.') => {
    setDetailMsg('Processing...');
    try {
      const res = await authedFetch(path, { method: 'POST', body: JSON.stringify(body || {}) });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) { setDetailMsg(data?.error?.message || 'Action failed.'); return null; }
      setDetailMsg(successMsg);
      if (expandedOrderId) {
        await loadDetail(expandedOrderId);
        await loadOrderWarnings(expandedOrderId);
      }
      await loadFirstPage({ silent: true });
      return data;
    } catch (err) {
      setDetailMsg(err?.message || 'Action failed.');
      return null;
    }
  }, [authedFetch, expandedOrderId, loadDetail, loadFirstPage, loadOrderWarnings]);

  // ── Status counts ──
  const loadStatusCounts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (applied.search) params.set('search', applied.search);
      const res = await authedFetch(`/api/v9/orders/status-counts?${params.toString()}`);
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) return;
      const counts = data?.result?.countsByStatus && typeof data.result.countsByStatus === 'object'
        ? data.result.countsByStatus : {};
      setStatusCounts(counts);
      setStatusTotalCount(Number(data?.result?.totalCount || 0));
    } catch (_) { /* keep previous */ }
  }, [applied.search, authedFetch]);

  // ── Initial load & filter changes ──
  useEffect(() => { loadFirstPage({ preferCache: true }); }, [loadFirstPage]);
  useEffect(() => { loadStatusCounts(); }, [loadStatusCounts]);

  // ── Auto-refresh every 60 s (pauses when detail open or tab hidden) ──
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (expandedOrderId) return; // user is reading a detail — don't disrupt
      loadFirstPage({ preferCache: false, silent: true });
      loadStatusCounts();
    };
    autoRefreshRef.current = setInterval(tick, 60000);
    return () => clearInterval(autoRefreshRef.current);
  }, [loadFirstPage, loadStatusCounts, expandedOrderId]);

  // Refresh immediately when user switches back to the tab (if idle >60 s)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (expandedOrderId) return;
      const staleSec = lastRefreshedAt ? (Date.now() - lastRefreshedAt) / 1000 : Infinity;
      if (staleSec >= 60) {
        loadFirstPage({ preferCache: false, silent: true });
        loadStatusCounts();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadFirstPage, loadStatusCounts, lastRefreshedAt, expandedOrderId]);

  // ── Last updated label ──
  const lastUpdatedLabel = useMemo(() => {
    if (!lastRefreshedAt) return '';
    const d = new Date(lastRefreshedAt);
    return `Updated ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }, [lastRefreshedAt]);

  // ── Apply filters ──
  const applyFilters = useCallback(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setApplied({ search: draftSearch.trim(), searchField: draftSearchField, status: draftStatus, warningType: draftWarningType });
    setExpandedOrderId('');
    setDetailData(null);
  }, [draftSearch, draftSearchField, draftStatus, draftWarningType]);

  const clearFilters = useCallback(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setDraftSearch('');
    setDraftSearchField('orderNumber');
    setDraftStatus('ALL');
    setDraftWarningType('ALL');
    setApplied({ search: '', searchField: 'orderNumber', status: 'ALL', warningType: 'ALL' });
    setExpandedOrderId('');
    setDetailData(null);
  }, []);

  // ── Derived detail values ──
  const selectedOrder = useMemo(() => {
    if (!detailData) return null;
    return detailData.order || detailData;
  }, [detailData]);

  const selectedLines = useMemo(() => {
    if (!detailData) return [];
    return Array.isArray(detailData.lines) ? detailData.lines : [];
  }, [detailData]);

  const orderStatus = useMemo(() => String(selectedOrder?.orderStatus || '').trim().toUpperCase(), [selectedOrder]);

  // Restock eligible only for CANCELLED / RTO_RECEIVED / RETURN_RECEIVED (same as V9)
  const isRestockStatusEligible = useMemo(() => (
    ['CANCELLED', 'RTO_RECEIVED', 'RETURN_RECEIVED'].includes(orderStatus)
  ), [orderStatus]);

  // Process PO eligible lines — only IMPORTED / ALLOCATING / PRODUCTION
  const processPoEligibleLines = useMemo(() => {
    if (!selectedLines.length) return [];
    if (!['IMPORTED', 'ALLOCATING', 'PRODUCTION'].includes(orderStatus)) return [];
    return selectedLines.filter((line) => {
      const inBatchQty = Math.max(0, Number(line?.inBatchQty || 0));
      const netReq = Math.max(0, Number(line?.netRequiredQty || 0));
      const received = Math.max(0, Number(line?.receivedQty || 0));
      const pending = Math.max(0, netReq - received - inBatchQty);
      if (inBatchQty > 1e-9) return false;
      if (pending <= 1e-9) return false;
      return true;
    }).map((line) => {
      const netReq = Math.max(0, Number(line?.netRequiredQty || 0));
      const received = Math.max(0, Number(line?.receivedQty || 0));
      return { ...line, qty: Math.max(0, netReq - received) };
    });
  }, [orderStatus, selectedLines]);

  const canProcessByPoEligible = useMemo(() => (
    canProcessPO && processPoEligibleLines.length > 0 && isPreShip(orderStatus)
  ), [canProcessPO, orderStatus, processPoEligibleLines]);

  const canMarkPackEligible = useMemo(() => {
    if (!selectedOrder) return false;
    return ['READY_TO_PACK', 'AWAITING_SHIPMENT_WEBHOOK'].includes(orderStatus);
  }, [orderStatus, selectedOrder]);

  const canCancelShipEligible = useMemo(() => {
    if (!selectedOrder) return false;
    return ['PACKED', 'IN_TRANSIT', 'READY_TO_PACK', 'AWAITING_SHIPMENT_WEBHOOK'].includes(orderStatus);
  }, [orderStatus, selectedOrder]);

  // ── Mark Used open ──
  const openMarkUsed = useCallback(async (line) => {
    if (!expandedOrderId) return;
    setMarkUsedLoading(true);
    setMarkUsedCtx({
      orderId: expandedOrderId,
      sku: String(line?.sku || '').trim().toUpperCase(),
      pendingQty: Math.max(0, Number(line?.netRequiredQty || 0) - Number(line?.receivedQty || 0) - Math.max(0, Number(line?.inBatchQty || 0))),
      productName: line?.productName || line?.name || line?.title || line?.sku || '-',
      productImage: String(line?.productImageUrl || line?.imageUrl || '').trim(),
    });
    setMarkUsedPieces([]);
    setMarkUsedOpen(true);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(expandedOrderId)}/lines/${encodeURIComponent(String(line?.sku || '').trim())}/stock-candidates`);
      const data = await parseJsonSafe(res);
      if (res.ok && data?.ok) {
        const result = data.result || {};
        setMarkUsedPieces(Array.isArray(result?.pieces) ? result.pieces : []);
        setMarkUsedCtx((prev) => ({
          ...(prev || {}),
          pendingQty: Number(result?.pendingQty || prev?.pendingQty || 0),
          orderedQty: Number(line?.netRequiredQty || result?.pendingQty || prev?.pendingQty || 0),
          allocationMode: String(result?.allocationMode || line?.allocationMode || '').trim().toUpperCase(),
          componentSummary: Array.isArray(result?.componentSummary) ? result.componentSummary : [],
          componentCandidates: Array.isArray(result?.componentCandidates) ? result.componentCandidates : [],
          productName: result?.productName || prev?.productName || line?.productName || line?.sku || '-',
          productImage: result?.productImageUrl || prev?.productImage || '',
        }));
      }
    } finally {
      setMarkUsedLoading(false);
    }
  }, [authedFetch, expandedOrderId]);

  const submitMarkUsed = useCallback(async (consume) => {
    if (!markUsedCtx?.sku || !expandedOrderId) return;
    setMarkUsedSubmitting(true);
    const data = await callAction(
      `/api/v9/orders/${encodeURIComponent(expandedOrderId)}/mark-used`,
      { items: [{ sku: markUsedCtx.sku, consume }] },
      'Marked used.'
    );
    if (data) {
      setMarkUsedOpen(false); setMarkUsedCtx(null); setMarkUsedPieces([]);
      const jobs = data?.result?.printJobs || [];
      for (const job of jobs) { printSticker?.(job.payload)?.catch?.(() => {}); }
    }
    setMarkUsedSubmitting(false);
  }, [callAction, expandedOrderId, markUsedCtx, printSticker]);

  // ── Restock (B3 unified piece-level picker — Restock, Excess, and Undo all open this) ──
  const openRestockPicker = useCallback((line) => {
    const orderNumber = selectedOrder ? getOrderNumber(selectedOrder) : expandedOrderId;
    setRestockPickerCtx({
      sku: String(line?.sku || '').trim().toUpperCase(),
      orderId: expandedOrderId,
      orderNumber: String(orderNumber || '').trim(),
      productImage: String(line?.productImageUrl || line?.imageUrl || '').trim(),
      productName: line?.productName || line?.name || line?.title || '',
    });
    setRestockPickerOpen(true);
  }, [expandedOrderId, selectedOrder]);

  const closeRestockPicker = useCallback(() => {
    setRestockPickerOpen(false);
    setRestockPickerCtx({ sku: '', orderId: '', orderNumber: '', productImage: '', productName: '' });
  }, []);

  // ── Process PO ──
  const openProcessPo = useCallback(() => {
    if (!canProcessByPoEligible) return;
    const notInStock = processPoEligibleLines.filter((l) => !l.inStockEligible).map((l) => l.sku);
    setProcessPoLines(processPoEligibleLines);
    setProcessPoSelected(notInStock.length > 0 ? notInStock : processPoEligibleLines.map((l) => l.sku));
    setProcessPoOpen(true);
  }, [canProcessByPoEligible, processPoEligibleLines]);

  const togglePoSku = useCallback((sku) => {
    setProcessPoSelected((prev) => prev.includes(sku) ? prev.filter((x) => x !== sku) : [...prev, sku]);
  }, []);

  const submitProcessPo = useCallback(async () => {
    if (!expandedOrderId) return;
    const selected = processPoLines.filter((l) => processPoSelected.includes(l.sku));
    if (selected.length === 0) { setDetailMsg('Select at least one line.'); return; }
    setProcessPoSubmitting(true);
    const ok = await callAction(
      `/api/v9/orders/${encodeURIComponent(expandedOrderId)}/process-by-po`,
      { items: selected.map((l) => ({ sku: l.sku, qty: l.qty })) },
      'Process by PO submitted.'
    );
    if (ok) { setProcessPoOpen(false); setProcessPoSelected([]); }
    setProcessPoSubmitting(false);
  }, [callAction, expandedOrderId, processPoLines, processPoSelected]);

  // ── Pack ──
  const openPack = useCallback(() => {
    setPackAwb('');
    setPackPartner('delhivery');
    setPackOpen(true);
  }, []);

  const submitPack = useCallback(async () => {
    if (!expandedOrderId) return;
    if (!String(packAwb || '').trim()) { setDetailMsg('AWB is required.'); return; }
    setPackSubmitting(true);
    const packages = [{
      packageRef: `PKG-${Date.now()}`,
      lineItems: selectedLines.map((l) => ({ sku: l.sku, qty: Number(l.netRequiredQty || 0) })),
    }];
    const ok = await callAction(
      `/api/v9/orders/${encodeURIComponent(expandedOrderId)}/pack`,
      { shipmentPartner: String(packPartner || 'delhivery').toLowerCase(), awb: String(packAwb).trim(), packages },
      'Order packed.'
    );
    if (ok) setPackOpen(false);
    setPackSubmitting(false);
  }, [callAction, expandedOrderId, packAwb, packPartner, selectedLines]);

  // ── Cancel shipment ──
  const submitCancelShipment = useCallback(async () => {
    if (!cancelConfirmOrderId) return;
    setCancelSubmitting(true);
    const ok = await callAction(
      `/api/v9/orders/${encodeURIComponent(cancelConfirmOrderId)}/cancel-request`,
      {},
      'Shipment cancelled.'
    );
    if (ok) setCancelConfirmOrderId('');
    setCancelSubmitting(false);
  }, [callAction, cancelConfirmOrderId]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="on-page">
      {/* ── Header ── */}
      <div className="on-header">
        <div className="on-header-left">
          <h1 className="on-title">New Orders</h1>
          {lastUpdatedLabel && <span className="on-updated">{lastUpdatedLabel}</span>}
        </div>
        <button
          className="on-refresh-btn"
          onClick={() => loadFirstPage()}
          title="Refresh orders"
          disabled={loading}
        >
          <span className={`on-refresh-icon${loading ? ' spinning' : ''}`}>↻</span>
          <span className="on-refresh-label">Refresh</span>
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="on-filters">
        <div className="on-search-row">
          <select
            className="on-search-field-select"
            value={draftSearchField}
            onChange={(e) => {
              const field = e.target.value;
              setDraftSearchField(field);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              const trimmed = draftSearch.trim();
              if (trimmed.length >= 3) {
                searchDebounceRef.current = setTimeout(() => {
                  setApplied((prev) => ({ ...prev, search: trimmed, searchField: field }));
                  setExpandedOrderId('');
                  setDetailData(null);
                }, 500);
              }
            }}
          >
            <option value="orderNumber">Order Number</option>
          </select>
          <input
            className="on-search-input"
            type="text"
            placeholder="Search (min 3 chars)…"
            value={draftSearch}
            onChange={(e) => {
              const val = e.target.value;
              setDraftSearch(val);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              const trimmed = val.trim();
              if (trimmed.length === 0) {
                setApplied((prev) => ({ ...prev, search: '', searchField: draftSearchField }));
                setExpandedOrderId('');
                setDetailData(null);
                return;
              }
              if (trimmed.length < 3) return;
              searchDebounceRef.current = setTimeout(() => {
                setApplied((prev) => ({ ...prev, search: trimmed, searchField: draftSearchField }));
                setExpandedOrderId('');
                setDetailData(null);
              }, 500);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
          />
          <button className="on-btn on-btn-primary" onClick={applyFilters}>Search</button>
          {(applied.search || applied.status !== 'ALL') && (
            <button className="on-btn on-btn-ghost" onClick={clearFilters}>Clear</button>
          )}
        </div>
        <div className="on-pills-wrap">
          {STATUS_PILLS.map((pill) => {
            const count = pill.value === 'ALL' ? statusTotalCount : Number(statusCounts?.[pill.value] || 0);
            // Hide pills with 0 orders (except ALL and currently selected)
            if (pill.value !== 'ALL' && count === 0 && draftStatus !== pill.value) return null;
            return (
              <button
                key={pill.value}
                className={`on-pill${draftStatus === pill.value ? ' active' : ''}`}
                onClick={() => {
                  setDraftStatus(pill.value);
                  setDraftWarningType('ALL');
                  setApplied((prev) => ({ ...prev, status: pill.value, warningType: 'ALL' }));
                }}
              >
                {pill.label}
                {count > 0 && <span className="on-pill-count">{count}</span>}
              </button>
            );
          })}
        </div>
        {applied.status === 'WARNINGS' && (
          <div className="on-sub-pills-wrap" style={{ marginTop: '0.6rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap', borderTop: '1px dashed #e2e5ea', paddingTop: '0.6rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--on-text-muted)', display: 'inline-flex', alignItems: 'center', marginRight: '4px', fontWeight: '600' }}>Warning Type:</span>
            {WARNING_SUB_PILLS.map((subPill) => (
              <button
                key={subPill.value}
                className={`on-pill${draftWarningType === subPill.value ? ' active' : ''}`}
                style={{ fontSize: '0.74rem', padding: '0.2rem 0.6rem' }}
                onClick={() => {
                  setDraftWarningType(subPill.value);
                  setApplied((prev) => ({ ...prev, warningType: subPill.value }));
                }}
              >
                {subPill.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── List message ── */}
      {listMsg && <div className="on-list-msg">{listMsg}</div>}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="on-skeleton-wrap">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="on-skeleton-card" />
          ))}
        </div>
      )}

      {/* ── Order cards ── */}
      {!loading && (
        <div className="on-cards">
          {rows.length === 0 && <div className="on-empty">No orders found.</div>}
          {rows.map((row) => {
            const orderId = String(row?.id || '').trim();
            const orderNum = getOrderNumber(row);
            const status = String(row?.orderStatus || '').trim().toUpperCase();
            const createdMs = getSourceCreatedAtMs(row);
            const isExpanded = expandedOrderId === orderId;
            const tone = statusTone(status);

            const hasWarning = row?.flags?.hasOpenStaleWarning === true || Number(row?.flags?.staleWarningCount || 0) > 0;

            return (
              <div key={orderId} className={`on-card${isExpanded ? ' expanded' : ''}${hasWarning ? ' has-warning' : ''}`}>
                {/* ── Card header ── */}
                <div className="on-card-header-wrap">
                  <button
                    className="on-card-header"
                    onClick={() => toggleExpand(orderId)}
                    aria-expanded={isExpanded}
                  >
                    <div className="on-card-header-left">
                      <div className="on-order-num-row">
                        <span className="on-order-num">{orderNum}</span>
                        <button
                          className="on-copy-btn"
                          title="Copy order number"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(orderNum).then(() => {
                              setCopiedOrderId(orderId);
                              setTimeout(() => setCopiedOrderId(''), 1500);
                            }).catch(() => {});
                          }}
                        >
                          {copiedOrderId === orderId ? '✓' : '⎘'}
                        </button>
                      </div>
                      <span className="on-created-at">{formatCreatedAt(createdMs)}</span>
                    </div>
                    <div className="on-card-header-right">
                      {hasWarning && (
                        <span className="on-warning-badge" title="Order has active stale warning">
                          ⚠️ Warning
                        </span>
                      )}
                      <span className={`on-status-badge on-tone-${tone}`}>{formatStatus(status)}</span>
                      <span className="on-chevron">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </button>
                </div>

                {/* ── Card body (expanded) ── */}
                {isExpanded && (
                  <div className="on-card-body">
                    {detailLoading && <div className="on-detail-loading">Loading order details…</div>}
                    {detailMsg && <div className={`on-detail-msg${detailMsg === 'Processing...' ? ' muted' : ''}`}>{detailMsg}</div>}

                    {/* Shipment / Packages */}
                    {!detailLoading && (() => {
                      const orderShipments = Array.isArray(selectedOrder?.shipments) ? selectedOrder.shipments : [];
                      const legacyShip = selectedOrder?.shipment;
                      const showShipments = orderShipments.length > 0
                        ? orderShipments
                        : (legacyShip?.awb ? [{
                            awb: legacyShip.awb,
                            partner: legacyShip.partner || 'Other',
                            status: legacyShip.status || selectedOrder.orderStatus,
                            externalStatus: legacyShip.externalStatus,
                          }] : []);

                      if (showShipments.length === 0) return null;

                      return (
                        <div className="on-packages-block" style={{ marginBottom: '1rem', border: '1px solid var(--on-border)', padding: '12px', borderRadius: 'var(--on-radius-sm)', background: '#fafafa' }}>
                          <strong style={{ fontSize: '0.85rem', color: 'var(--on-text)', display: 'block', marginBottom: '8px' }}>Packages / Shipments</strong>
                          <div className="on-packages-list">
                            {showShipments.map((pkg, idx) => (
                              <div className="on-package-item" key={pkg.awb || idx} style={{ borderBottom: idx === showShipments.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.05)', paddingBottom: '8px', marginBottom: idx === showShipments.length - 1 ? '0' : '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <span style={{ fontWeight: '600', fontSize: '0.85rem' }}>Pkg {idx + 1}: </span>
                                    <span className="mono" style={{ background: '#eee', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8em', fontFamily: 'monospace' }}>{pkg.awb}</span>
                                    <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: 'var(--on-text-muted)' }}>({String(pkg.partner || 'Other').toUpperCase()})</span>
                                  </div>
                                  <span className={`on-status-badge on-tone-${statusTone(pkg.status || 'shipped')}`} style={{ fontSize: '0.75rem', padding: '2px 6px' }}>
                                    {formatStatus(pkg.status || 'shipped')}
                                  </span>
                                </div>
                                {pkg.externalStatus && (
                                  <div style={{ fontSize: '0.8rem', color: 'var(--on-text-muted)', marginTop: '4px' }}>
                                    <strong>Tracking:</strong> {pkg.externalStatus}
                                  </div>
                                )}
                                {/* Status Selector Dropdown for manual update */}
                                {canManualStatus && pkg.status !== 'DELIVERED' && pkg.status !== 'RTO_RECEIVED' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--on-text-muted)' }}>Update Status:</span>
                                    <select
                                      value={pkg.status || 'SHIPPED'}
                                      style={{ padding: '2px 6px', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
                                      onChange={async (e) => {
                                        const newStatus = e.target.value;
                                        if (!newStatus) return;
                                        const remarks = window.prompt(`Update package ${pkg.awb} status to ${formatStatus(newStatus)}?\nEnter optional tracking remarks:`, '');
                                        if (remarks === null) return; // cancelled
                                        await callAction(`/api/v9/orders/${encodeURIComponent(orderId)}/manual-status`, {
                                          awb: pkg.awb,
                                          status: newStatus,
                                          remarks: remarks.trim()
                                        }, `Package status updated to ${formatStatus(newStatus)}.`);
                                      }}
                                    >
                                      <option value="SHIPPED">In Transit / Shipped</option>
                                      <option value="DELIVERED">Delivered</option>
                                      <option value="RTO_IN_PROGRESS">RTO In Progress</option>
                                      <option value="RTO_RECEIVED">RTO Received</option>
                                    </select>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Line items */}
                    {!detailLoading && selectedLines.length > 0 && (
                      <div className="on-lines">
                        <div className="on-lines-header">
                          <span>Item</span>
                          <span>Ordered</span>
                          <span>Received</span>
                          <span>Actions</span>
                        </div>
                        {selectedLines.map((line) => {
                          const sku = String(line?.sku || '').trim().toUpperCase();
                          const elig = lineEligibility[sku] || {};
                          const netReq = Math.max(0, Number(line?.netRequiredQty || 0));
                          const received = Math.max(0, Number(line?.receivedQty || 0));
                          const inBatch = Math.max(0, Number(line?.inBatchQty || 0));
                          const cancelledQty = Math.max(0, Number(line?.cancelledQty || 0));
                          const isFullyCancelled = netReq <= 1e-9 && cancelledQty > 0;
                          const restocked = Math.max(0, Number(line?.restockedQty || 0));
                          const restockPendingQty = Math.max(0, Number(line?.restockPendingQty || 0));
                          const excessRestocked = Math.max(0, Number(line?.excessRestockedQty || 0));
                          const excessPendingQty = Math.max(0, received - getExcessTargetQty(line)) - excessRestocked;
                          const usedOpenQty = Math.max(0, Number(detailData?.usedOpenBySku?.[sku] || 0));
                          const lineActivity = detailData?.lineActivityByLine?.[line.id] || {};
                          const awaitingBatchIds = Array.isArray(lineActivity?.awaitingBatchIds) ? lineActivity.awaitingBatchIds : [];
                          const receivedBatchIds = Array.isArray(lineActivity?.receivedBatchIds) ? lineActivity.receivedBatchIds : [];
                          const usedPieceIdsFromLine = Array.isArray(lineActivity?.usedPieceIds) ? lineActivity.usedPieceIds : [];
                          const usedSkuPieceMap = detailData?.usedOpenBySkuPiece?.[sku] || {};
                          const usedPieceIdsFromSku = Object.keys(usedSkuPieceMap).filter((pid) => Number(usedSkuPieceMap[pid] || 0) > 1e-9);
                          const usedPieceIds = usedPieceIdsFromLine.length > 0 ? usedPieceIdsFromLine : usedPieceIdsFromSku;
                          const canLineMarkUsed = canMarkUsed && elig.canMarkUsed === true;
                          const canLineUndoUsed = canUndoUsed && isPreShip(orderStatus) && usedOpenQty > 1e-9;
                          const canLineRestock = canRestock && isRestockStatusEligible && (restockPendingQty - restocked) > 1e-9;
                          const canLineUndoRestock = canUndoRestock && isRestockStatusEligible && restocked > 1e-9;
                          const hasExcess = excessPendingQty > 1e-4;
                          const imgUrl = String(line?.productImageUrl || line?.imageUrl || elig?.productImageUrl || '').trim();
                          const title = String(line?.productName || line?.name || line?.title || sku).trim();

                          return (
                            <div key={sku} className="on-line-row">
                              <div className="on-line-item">
                                {imgUrl
                                  ? <img className="on-line-img" src={imgUrl} alt={sku} loading="lazy" />
                                  : <div className="on-line-img-placeholder" />
                                }
                                <div className="on-line-info">
                                  <span className="on-line-title" title={title} style={isFullyCancelled ? { textDecoration: 'line-through', opacity: 0.6 } : {}}>{title}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                    <span className="on-line-sku" style={isFullyCancelled ? { textDecoration: 'line-through', opacity: 0.6 } : {}}>{sku}</span>
                                    {isFullyCancelled && (
                                      <span style={{
                                        background: '#fee2e2',
                                        color: '#991b1b',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        fontSize: '0.7em',
                                        fontWeight: 'bold'
                                      }}>
                                        Cancelled
                                      </span>
                                    )}
                                  </div>
                                  {cancelledQty > 0 && (
                                    <span className="on-line-subtext red" style={{ color: '#dc2626', fontWeight: '500' }}>
                                      ⚠️ Cancelled: {formatQty(cancelledQty)}m
                                    </span>
                                  )}
                                  {awaitingBatchIds.length > 0 && (
                                    <span className="on-line-subtext amber">Awaiting in Batch: {compactIdList(awaitingBatchIds)}</span>
                                  )}
                                  {receivedBatchIds.length > 0 && (
                                    <span className="on-line-subtext green">Received from Batch: {compactIdList(receivedBatchIds)}</span>
                                  )}
                                  {usedPieceIds.length > 0 && (
                                    <span className="on-line-subtext blue">Used from: {compactIdList(usedPieceIds)}</span>
                                  )}
                                  {inBatch > 1e-9 && awaitingBatchIds.length === 0 && (
                                    <span className="on-line-subtext muted">In Batch: {formatQty(inBatch)}</span>
                                  )}
                                </div>
                              </div>
                              <div className="on-line-qty">
                                <span className="on-qty-label">Ordered</span>
                                <span className="on-qty-val">{formatQty(netReq)}</span>
                              </div>
                              <div className="on-line-qty">
                                <span className="on-qty-label">Received</span>
                                <span className={`on-qty-val${received >= netReq - 1e-9 ? ' done' : ''}`}>{formatQty(received)}</span>
                              </div>
                              <div className="on-line-actions">
                                {canLineMarkUsed && (
                                  <button className="on-action-sm primary" onClick={() => openMarkUsed(line)}>
                                    Mark Used
                                  </button>
                                )}
                                {(canLineRestock || canLineUndoRestock || (canRestock && hasExcess)) && (
                                  <button className="on-action-sm" onClick={() => openRestockPicker(line)}>
                                    {canLineRestock || (canRestock && hasExcess) ? 'Restock' : 'Undo'}
                                  </button>
                                )}
                                {canLineUndoUsed && (
                                  <button className="on-action-sm ghost" onClick={async () => {
                                    const pieceEntries = Object.entries(usedSkuPieceMap).filter(([, q]) => Number(q || 0) > 0);
                                    if (pieceEntries.length === 0) { setDetailMsg(`No used-piece breakdown found for ${sku}.`); return; }
                                    const items = pieceEntries.map(([stockPieceId, q]) => ({
                                      sku,
                                      stockPieceId,
                                      qty: Number(Number(q || 0).toFixed(3)),
                                    })).filter((x) => x.qty > 0);
                                    if (items.length === 0) { setDetailMsg(`No undo quantity available for ${sku}.`); return; }
                                    if (!window.confirm(`Undo mark used for ${sku} (${formatQty(usedOpenQty)}m)?`)) return;
                                    await callAction(
                                      `/api/v9/orders/${encodeURIComponent(expandedOrderId)}/undo-mark-used`,
                                      { items },
                                      'Undo mark used done.'
                                    );
                                  }}>
                                    Undo Used
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Warnings panel */}
                    {((detailWarningsLoading && selectedOrder?.flags?.hasOpenStaleWarning) || detailWarnings.length > 0) && (
                      <div className="on-warning-panel" style={{ marginTop: '1rem', background: '#fff9e6', border: '1px solid #ffe699', padding: '12px', borderRadius: 'var(--on-radius-sm)', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <strong style={{ color: '#b78103', fontSize: '0.85rem' }}>Warning ({detailWarningsLoading ? 'Checking' : detailWarnings.length})</strong>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {detailWarnings.map((warning) => {
                            const severity = String(warning?.severity || 'WARNING').trim().toUpperCase();
                            const ageHours = Number(warning?.ageHours || 0);
                            const maxHours = Number(warning?.maxHours || 0);
                            const reason = String(warning?.reason || warning?.message || '').trim() || 'Warning reason unavailable.';
                            const suggestedAction = String(warning?.suggestedAction || '').trim();
                            const externalStatus = String(warning?.externalStatus || warning?.trackingStatus || warning?.shipmentStatus || '').trim();
                            const partner = String(warning?.partner || warning?.shipmentPartner || '').trim();
                            const awb = String(warning?.awb || warning?.trackingNumber || '').trim();
                            return (
                              <div key={warning.id || `${warning.orderId}-${reason}`} style={{ background: '#fff', border: '1px solid #eee', padding: '8px', borderRadius: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                  <div>
                                    <span style={{
                                      background: severity === 'CRITICAL' ? '#fde8e8' : '#fef3c7',
                                      color: severity === 'CRITICAL' ? '#9b1c1c' : '#92400e',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontSize: '0.75em',
                                      fontWeight: 'bold',
                                      marginRight: '8px'
                                    }}>{severity}</span>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: 'var(--on-text)' }}>{reason}</span>
                                  </div>
                                  {canSnoozeWarning && warning.id && !warning.resolved && (
                                    <button
                                      type="button"
                                      className="on-btn on-btn-ghost"
                                      style={{ padding: '2px 8px', fontSize: '0.8em', border: '1px solid #ccc', background: '#fff', height: 'auto', minHeight: 'unset' }}
                                      onClick={async () => {
                                        const hoursStr = window.prompt('Enter snooze duration in hours (e.g. 24, 48, 168):', '48');
                                        if (hoursStr === null) return;
                                        const hours = parseInt(hoursStr, 10);
                                        if (isNaN(hours) || hours <= 0) {
                                          alert('Please enter a valid positive number of hours.');
                                          return;
                                        }
                                        const snoozeReason = window.prompt('Enter snooze reason (optional):', '');
                                        if (snoozeReason === null) return;
                                        const ok = await callAction(`/api/v9/admin/stale-watchlist/${encodeURIComponent(warning.id)}/snooze`, {
                                          hours,
                                          reason: snoozeReason.trim()
                                        }, 'Warning snoozed successfully.');
                                        if (ok) {
                                          loadOrderWarnings(orderId);
                                        }
                                      }}
                                    >
                                      Snooze
                                    </button>
                                  )}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--on-text-muted)', marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                  {Number.isFinite(ageHours) && ageHours > 0 && (
                                    <span>Age: {formatWarningAge(ageHours)}{Number.isFinite(maxHours) && maxHours > 0 ? ` / ${formatWarningAge(maxHours)}` : ''}</span>
                                  )}
                                  {suggestedAction && <span>Action: {formatStatus(suggestedAction)}</span>}
                                  {awb && <span>AWB: {awb}</span>}
                                  {partner && <span>Partner: {partner.toUpperCase()}</span>}
                                  {externalStatus && <span>Tracking: {externalStatus}</span>}
                                </div>
                                {(() => {
                                  const status = String(warning?.orderStatus || '').trim().toUpperCase();
                                  let guidelineText = '';
                                  let actionButtons = [];

                                  if (status === 'IN_TRANSIT') {
                                    guidelineText = "This package has been in transit for more than 7 days. Check the courier status online, contact the customer, or contact the courier partner to escalate this.";
                                    if (awb) {
                                      const trackUrl = String(partner).toLowerCase() === 'delhivery'
                                        ? `https://www.delhivery.com/track/package/${awb}`
                                        : `https://shiprocket.co/tracking/${awb}`;
                                      actionButtons.push(
                                        <a
                                          key="track"
                                          href={trackUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="on-btn on-btn-ghost"
                                          style={{ padding: '2px 8px', fontSize: '0.78em', border: '1px solid #4f46e5', color: '#4f46e5', background: '#fff', textDecoration: 'none', height: 'auto', minHeight: 'unset', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                          Track Courier ↗
                                        </a>
                                      );
                                    }
                                  } else if (status === 'RTO_IN_PROGRESS') {
                                    guidelineText = "The shipment failed delivery and is returning. Check if the returned fabric has arrived at the warehouse to restock it. If it is in return transit, contact the shipping partner and escalate this.";
                                    if (awb) {
                                      const trackUrl = String(partner).toLowerCase() === 'delhivery'
                                        ? `https://www.delhivery.com/track/package/${awb}`
                                        : `https://shiprocket.co/tracking/${awb}`;
                                      actionButtons.push(
                                        <a
                                          key="track"
                                          href={trackUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="on-btn on-btn-ghost"
                                          style={{ padding: '2px 8px', fontSize: '0.78em', border: '1px solid #4f46e5', color: '#4f46e5', background: '#fff', textDecoration: 'none', height: 'auto', minHeight: 'unset', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                          Track RTO ↗
                                        </a>
                                      );
                                    }
                                    if (canManualStatus && orderId) {
                                      actionButtons.push(
                                        <button
                                          key="rto-received"
                                          type="button"
                                          className="on-btn on-btn-primary"
                                          style={{ padding: '2px 8px', fontSize: '0.78em', height: 'auto', minHeight: 'unset' }}
                                          onClick={async () => {
                                            if (!window.confirm(`Mark order ${orderId} as RTO received?`)) return;
                                            const ok = await callAction(`/api/v9/orders/${encodeURIComponent(orderId)}/rto-received`, {}, 'Marked RTO received.');
                                            if (ok) {
                                              loadOrderWarnings(orderId);
                                            }
                                          }}
                                        >
                                          Mark RTO Received
                                        </button>
                                      );
                                    }
                                  } else if (status === 'READY_TO_PACK') {
                                    guidelineText = "Fabric is ready. Pack and ship this order.";
                                    if (canMarkPackEligible && canMarkPacked) {
                                      actionButtons.push(
                                        <button
                                          key="pack"
                                          type="button"
                                          className="on-btn on-btn-primary"
                                          style={{ padding: '2px 8px', fontSize: '0.78em', height: 'auto', minHeight: 'unset' }}
                                          onClick={openPack}
                                        >
                                          Pack / Ship Now
                                        </button>
                                      );
                                    }
                                  } else if (status === 'PRODUCTION') {
                                    guidelineText = "Fabric is in production beyond SLA. Check the production status or contact the fabric vendor.";
                                  } else if (status === 'ALLOCATING') {
                                    guidelineText = "Fabric is not yet fully allocated. Check available inventory or allocate stock pieces.";
                                  }

                                  if (!guidelineText && actionButtons.length === 0) return null;

                                  return (
                                    <div style={{ marginTop: '8px', padding: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                                      {guidelineText && (
                                        <div style={{ fontSize: '0.78rem', color: '#475569', marginBottom: actionButtons.length > 0 ? '6px' : '0', lineHeight: '1.4' }}>
                                          <strong>Guide:</strong> {guidelineText}
                                        </div>
                                      )}
                                      {actionButtons.length > 0 && (
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                                          {actionButtons}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Order-level actions */}
                    {!detailLoading && (
                      <div className="on-order-actions">
                        {canProcessByPoEligible && (
                          <button className="on-btn on-btn-primary" onClick={openProcessPo}>
                            Process by PO
                          </button>
                        )}
                        {canMarkPackEligible && canMarkPacked && (
                          <button className="on-btn on-btn-primary" onClick={openPack}>
                            Pack / Ship
                          </button>
                        )}
                        {canCancelShipEligible && canCancelShipment && (
                          <button className="on-btn on-btn-warn" onClick={() => setCancelConfirmOrderId(orderId)}>
                            Cancel Shipment
                          </button>
                        )}
                        {canUndoProcessPO && row?.poLastBatchId && isPreShip(status) && (
                          <button className="on-btn on-btn-ghost" onClick={async () => {
                            if (!window.confirm('Undo Process by PO?')) return;
                            await callAction(
                              `/api/v9/orders/${encodeURIComponent(orderId)}/undo-process-po`,
                              {},
                              'Process by PO undone.'
                            );
                          }}>
                            Undo PO
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Load more / pagination ── */}
      {!loading && hasMore && (
        <div className="on-load-more-wrap">
          <button className="on-btn on-btn-ghost on-load-more" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────────── */}

      {/* Mark Used Modal */}
      <MarkUsedModal
        isOpen={markUsedOpen}
        loading={markUsedLoading}
        submitting={markUsedSubmitting}
        context={markUsedCtx || {}}
        pieces={markUsedPieces}
        onClose={() => { setMarkUsedOpen(false); setMarkUsedCtx(null); setMarkUsedPieces([]); }}
        onSubmit={submitMarkUsed}
      />

      {/* Restock Picker (B3 unified) */}
      <RestockPickerModal
        open={restockPickerOpen}
        orderId={restockPickerCtx.orderId}
        sku={restockPickerCtx.sku}
        orderNumber={restockPickerCtx.orderNumber}
        productImage={restockPickerCtx.productImage}
        productName={restockPickerCtx.productName}
        authedFetch={authedFetch}
        onClose={closeRestockPicker}
        onDone={async () => {
          if (expandedOrderId) await loadDetail(expandedOrderId);
          await loadFirstPage();
        }}
      />

      {/* Process PO Modal */}
      {processPoOpen && (
        <div className="on-modal-overlay" onClick={() => setProcessPoOpen(false)}>
          <div className="on-modal" onClick={(e) => e.stopPropagation()}>
            <div className="on-modal-header">
              <h2>Process by PO</h2>
              <button className="on-modal-close" onClick={() => setProcessPoOpen(false)}>×</button>
            </div>
            <div className="on-modal-body">
              <p className="on-modal-hint">Select line items to send to production batch:</p>
              {processPoLines.map((line) => (
                <label key={line.sku} className="on-po-line-check">
                  <input
                    type="checkbox"
                    checked={processPoSelected.includes(line.sku)}
                    onChange={() => togglePoSku(line.sku)}
                  />
                  <div className="on-po-line-info">
                    {(line.productImageUrl || line.imageUrl) && (
                      <img className="on-po-line-img" src={line.productImageUrl || line.imageUrl} alt={line.sku} />
                    )}
                    <div>
                      <span className="on-po-line-sku">{line.sku}</span>
                      <span className="on-po-line-qty">Qty: {formatQty(line.qty)}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="on-modal-footer">
              <button className="on-btn on-btn-ghost" onClick={() => setProcessPoOpen(false)}>Cancel</button>
              <button
                className="on-btn on-btn-primary"
                disabled={processPoSubmitting || processPoSelected.length === 0}
                onClick={submitProcessPo}
              >
                {processPoSubmitting ? 'Submitting…' : 'Create Batch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pack Modal */}
      {packOpen && (
        <div className="on-modal-overlay" onClick={() => setPackOpen(false)}>
          <div className="on-modal" onClick={(e) => e.stopPropagation()}>
            <div className="on-modal-header">
              <h2>Pack / Ship Order</h2>
              <button className="on-modal-close" onClick={() => setPackOpen(false)}>×</button>
            </div>
            <div className="on-modal-body">
              <label className="on-field-label">Shipment Partner</label>
              <select
                className="on-select"
                value={packPartner}
                onChange={(e) => setPackPartner(e.target.value)}
              >
                <option value="delhivery">Delhivery</option>
                <option value="bluedart">Blue Dart</option>
                <option value="dtdc">DTDC</option>
                <option value="xpressbees">Xpressbees</option>
                <option value="other">Other</option>
              </select>
              <label className="on-field-label" style={{ marginTop: '1rem' }}>AWB Number <span className="on-required">*</span></label>
              <input
                className="on-input"
                type="text"
                placeholder="Enter AWB number"
                value={packAwb}
                onChange={(e) => setPackAwb(e.target.value)}
                autoFocus
              />
            </div>
            <div className="on-modal-footer">
              <button className="on-btn on-btn-ghost" onClick={() => setPackOpen(false)}>Cancel</button>
              <button
                className="on-btn on-btn-primary"
                disabled={packSubmitting || !String(packAwb || '').trim()}
                onClick={submitPack}
              >
                {packSubmitting ? 'Packing…' : 'Confirm Pack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Shipment Confirm */}
      {cancelConfirmOrderId && (
        <div className="on-modal-overlay" onClick={() => setCancelConfirmOrderId('')}>
          <div className="on-modal on-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="on-modal-header">
              <h2>Cancel Shipment</h2>
              <button className="on-modal-close" onClick={() => setCancelConfirmOrderId('')}>×</button>
            </div>
            <div className="on-modal-body">
              <p>Are you sure you want to cancel the shipment for this order? This cannot be undone.</p>
            </div>
            <div className="on-modal-footer">
              <button className="on-btn on-btn-ghost" onClick={() => setCancelConfirmOrderId('')}>No, go back</button>
              <button
                className="on-btn on-btn-danger"
                disabled={cancelSubmitting}
                onClick={submitCancelShipment}
              >
                {cancelSubmitting ? 'Cancelling…' : 'Yes, Cancel Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
